// src/components/SimpleComponents/SimpleUniqueKey.jsx
// *******************************************************************************
// SIMPLE UNIQUE KEY - Reusable search-on-blur input with hasFeedback
// *******************************************************************************
// A text input that searches an API endpoint on blur.
// Shows loading spinner while searching, green check if found.
// Configurable via props: searchUrl, searchParams, onFound, onNotFound.
// Pattern simplified from SimpleCnpj.jsx.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useRef, useMemo } from 'react';
import { Input } from 'antd';
import SimpleFormItem from './SimpleFormItem';
import { useSimpleFormContext } from './SimpleFormProvider';
import useFormElement from 'hooks/useFormElement';
import ValidationSuffix from 'components/JarvislyComponents/ValidationSuffix';
import axiosService from 'services/axiosService';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleUniqueKey = ({
  name,
  subdocumentField,
  searchUrl,
  searchParams,
  onFound,
  onNotFound,
  onClear,
  minLength = 1,
  disabled,
  style = {},
  maxLength,
  placeholder,
  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const lastSearchedRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null); // null = not searched, true = found, false = not found

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleChange = useCallback(
    e => {
      const value = e?.target?.value ?? '';
      const trimmed = value.trim();

      // If value changed from last search, reset state
      if (lastSearchedRef.current !== null && trimmed !== lastSearchedRef.current) {
        lastSearchedRef.current = null;
        setFound(null);
      }

      // If cleared, notify parent
      if (!trimmed && onClear) {
        onClear();
        lastSearchedRef.current = null;
        setFound(null);
      }
    },
    [onClear],
  );

  const handleBlur = useCallback(
    async e => {
      const value = (e?.target?.value ?? '').trim();

      // Skip if empty or too short
      if (!value || value.length < minLength) {
        if (!value && found !== null) {
          setFound(null);
          lastSearchedRef.current = null;
        }
        return;
      }

      // Skip if already searched this value
      if (lastSearchedRef.current === value) return;

      // Skip if no search config
      if (!searchUrl || typeof searchParams !== 'function') return;

      // Execute search
      setSearching(true);
      lastSearchedRef.current = value;

      try {
        const params = searchParams(value);
        const queryString = Object.entries(params)
          .filter(([, v]) => v != null)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&');

        const url = `${searchUrl}?${queryString}`;
        const [doc] = await axiosService.get({ url, returnAsObject: true });

        if (doc && doc._id) {
          setFound(true);
          onFound?.(doc);
        } else {
          setFound(false);
          onNotFound?.(value);
        }
      } catch {
        setFound(false);
        onNotFound?.(value);
      } finally {
        setSearching(false);
      }
    },
    [searchUrl, searchParams, minLength, onFound, onNotFound, found],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Status for ValidationSuffix: validating | success | '' (neutral)
  const status = useMemo(() => {
    if (searching) return 'validating';
    if (found === true) return 'success';
    return '';
  }, [searching, found]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <SimpleFormItem {...props} name={fullFieldName} elementProps={elementProps}>
      <Input
        ref={elementRef}
        id={flattenFieldName}
        disabled={isDisabled}
        maxLength={maxLength}
        placeholder={placeholder}
        style={{ height: 40, ...style }}
        onChange={handleChange}
        onBlur={handleBlur}
        suffix={<ValidationSuffix status={status} loading={searching} />}
      />
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleUniqueKey;
